// Reports page — entries list + range report behind a tab switcher.
// (The Soll-vs-Ist weekly chart lives on /dashboard now.)
// Filter state (date range, project, search, tags) is shared across tabs.
// The standalone /entries and /reports routes still exist as deep-links.
import { useQuery } from '@tanstack/react-query'
import { Navigate } from '@tanstack/react-router'
import { Download, List, PieChart, Plus, Search, X } from 'lucide-react'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { api, type TimeEntry } from '../lib/api'
import { groupEntriesByLocalDay } from '../lib/entry-groups'
import { gapMinutes, MIN_GAP_MINUTES } from '../lib/gaps'
import { cn, formatDuration } from '../lib/utils'
import { MiniPager, useEmbeddedPageSize } from './data-table'
import { EntryFormDialog } from './entry-form-dialog'
import { BreakRow, BulkActions, EntryListItem, formatEntryDate } from './entry-list'
import { RangeReportView } from './range-report-view'
import { TagPicker } from './tag-picker'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select } from './ui/select'

type TabId = 'list' | 'range'

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}
function daysAgoIso(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}
function firstOfMonthIso(): string {
  const d = new Date()
  d.setDate(1)
  return d.toISOString().slice(0, 10)
}

// Route + standalone page wrapper sind entfernt — die Seite ist nicht
// mehr in der Sidebar und auch keine eigene URL mehr. AuswertungPageBody
// bleibt als gemeinsame Implementierung für den eingebetteten Einsatz
// in CalendarInfoPanel ('Einträge'-Tab) und im Dashboard-Widget
// 'range-report' bestehen.
export function AuswertungPageBody({
  embedded = false,
  initialTab = 'list',
  lockedTab = false,
  hideTabs = false,
  initialFrom,
  initialTo,
  userId,
  readOnly = false,
}: {
  embedded?: boolean
  initialTab?: TabId
  /** Forbids tab switching — tab stays on initialTab even if user clicks. */
  lockedTab?: boolean
  /** Hides the tab bar entirely (e.g. for the range-report dashboard widget
   *  which only ever shows the range view). */
  hideTabs?: boolean
  /** Scope the list/report to ANOTHER user's entries (admin/leader viewing
   *  someone else's calendar). Undefined = session user. */
  userId?: string
  /** Lesemodus — blendet alle Bearbeiten/Löschen-Affordances aus. */
  readOnly?: boolean
  /** Override the default initial from/to dates (YYYY-MM-DD). Useful for
   *  the dashboard widget that ships a quick timeframe-switch above the
   *  embedded body — remount with a new key to apply a fresh range. */
  initialFrom?: string
  initialTo?: string
} = {}) {
  // In embedded mode the URL search params don't drive the tab — we
  // keep tab state local so the panel can swap between Liste / Range
  // without changing the route.
  const [localTab, setLocalTab] = useState<TabId>(initialTab)
  const tab = localTab
  const setTab = setLocalTab
  // Default the date window to "month-to-today" for reports, but to "last 7
  // days" for the list — since the list grows fast for active users.
  const [from, setFrom] = useState(
    initialFrom ?? (tab === 'list' ? daysAgoIso(7) : firstOfMonthIso()),
  )
  const [to, setTo] = useState(initialTo ?? todayIso())
  const [projectId, setProjectId] = useState<string>('')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [dialog, setDialog] = useState<{ open: boolean; entry: TimeEntry | null }>({
    open: false,
    entry: null,
  })

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects(),
    enabled: !!meData?.user,
  })
  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(),
    enabled: !!meData?.user,
  })
  const { data: tagsData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.listTags(),
    enabled: !!meData?.user,
  })
  const { data: entriesData, isLoading: entriesLoading } = useQuery({
    queryKey: ['entries', { from, to, projectId, search: debouncedSearch, tagFilter, userId }],
    queryFn: () =>
      api.listEntries({
        // Local-midnight boundaries (not UTC): `${from}T00:00:00` without a Z
        // parses in the browser's timezone, so the window matches the days the
        // user sees. A naive `…Z` cut the first day's local 00:00–02:00 (CEST)
        // and pulled the following day's early hours in.
        from: new Date(`${from}T00:00:00`).toISOString(),
        to: new Date(`${to}T23:59:59.999`).toISOString(),
        projectId: projectId || undefined,
        search: debouncedSearch || undefined,
        tagIds: tagFilter.length > 0 ? tagFilter : undefined,
        userId: userId || undefined,
      }),
    enabled: !!meData?.user && tab === 'list',
  })
  const { data: rangeData, isLoading: rangeLoading } = useQuery({
    queryKey: ['reports', 'range', { from, to, projectId }],
    queryFn: () => api.rangeReport({ from, to, projectId: projectId || undefined }),
    enabled: !!meData?.user && tab === 'range',
  })

  const projects = projectsData?.projects ?? []
  const customers = customersData?.customers ?? []
  const tags = tagsData?.tags ?? []
  const projectMap = useMemo(() => new Map(projects.map((p) => [p.id, p])), [projects])
  const customerMap = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers])
  const tagMap = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags])

  // Stabilise the entries reference: only update when the underlying data
  // actually changes.  Without this, `entriesData?.entries ?? []` creates a
  // fresh empty array on every render when `entriesData` is undefined, which
  // triggers the selection-sync effect below and causes an infinite re-render.
  const rawEntries = entriesData?.entries
  const entries = useMemo(() => rawEntries ?? [], [rawEntries])

  const totalMs = entries.reduce((acc, e) => {
    const end = e.endTime ? new Date(e.endTime).getTime() : Date.now()
    return acc + (end - new Date(e.startTime).getTime())
  }, 0)

  // Gruppierung nach LOKALEM Tag — die frühere UTC-Gruppierung
  // (`toISOString().slice(0, 10)`) hängte einen 00:47-lokal-Eintrag unter
  // den Vortag (D8). Getestet in lib/entry-groups.test.ts.
  const groups = useMemo(() => groupEntriesByLocalDay(entries), [entries])

  // Embedded view: paginate by day-group so the calendar info-panel stays
  // usable with hundreds of entries. Full /auswertung keeps the long scroll.
  // Default 5 day-groups per page; user can change via the size picker.
  const [embeddedDaysPerPage, setEmbeddedDaysPerPage] = useEmbeddedPageSize('auswertung-list', 5)
  const [listPage, setListPage] = useState(1)
  const totalListPages = embedded ? Math.max(1, Math.ceil(groups.length / embeddedDaysPerPage)) : 1
  useEffect(() => {
    if (listPage > totalListPages) setListPage(1)
  }, [listPage, totalListPages])
  const visibleGroups = embedded
    ? groups.slice((listPage - 1) * embeddedDaysPerPage, listPage * embeddedDaysPerPage)
    : groups

  // Drop selection rows that no longer match the filtered list.
  // We track a stringified key of entry IDs so this only fires when the
  // actual set of entries changes — not on every render.
  const entryIds = useMemo(() => entries.map((e) => e.id), [entries])
  const prevEntryIdsRef = useRef(entryIds)
  useEffect(() => {
    if (prevEntryIdsRef.current === entryIds) return
    prevEntryIdsRef.current = entryIds
    setSelected((prev) => {
      const next = new Set<string>()
      for (const id of entryIds) if (prev.has(id)) next.add(id)
      if (next.size === prev.size && [...next].every((id) => prev.has(id))) return prev
      return next
    })
  }, [entryIds])

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const showFilters = tab === 'list' || tab === 'range'
  const showSearchTags = tab === 'list'

  // In embedded mode the standalone page-header is empty (no H1, no
  // create-button, no exports). Suppress its wrapper so we don't reserve
  // vertical space + a border for nothing — gives the chart more room.
  // Embedded mode no longer renders any header controls (Σ Zeitraum and
  // exports both suppressed), so the wrapper is always empty there.
  // Embedded mode hat keinen eigenen Header — der Dashboard-Widget-
  // Wrapper bringt seine eigene Zeitraum-Auswahl + Export-Dropdown mit.
  const showEmbeddedHeader = false

  return (
    <>
      <div className={cn(embedded ? 'space-y-2' : 'space-y-6 animate-fade-in')}>
        {(!embedded || showEmbeddedHeader) && (
          <div
            className={cn(
              'flex items-center justify-between gap-2 border-b border-border-subtle',
              embedded ? 'pb-2' : 'pb-3',
            )}
          >
            {!embedded && (
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-lg font-medium tracking-tight text-text-primary shrink-0">
                  Reports
                </h1>
              </div>
            )}
            <div
              className={cn('flex items-center gap-2 shrink-0 flex-wrap', embedded && 'ml-auto')}
            >
              {tab === 'list' && !embedded && (
                <div className="text-right">
                  <p className="text-2xs uppercase tracking-wider text-text-tertiary leading-none">
                    Σ Zeitraum
                  </p>
                  <p className="font-mono tabular-nums text-base font-light text-accent">
                    {formatDuration(totalMs)}
                  </p>
                </div>
              )}
              {!embedded && (
                <Button onClick={() => setDialog({ open: true, entry: null })} size="lg">
                  <Plus className="h-4 w-4" /> Eintrag
                </Button>
              )}
              {/* CSV/PDF export — sichtbar in jeder Range-Ansicht,
                  inkl. Dashboard-Widget (embedded). Der Klick öffnet
                  in beiden Fällen einen neuen Tab mit Download. */}
              {tab === 'range' && (
                <div className="inline-flex gap-2">
                  <a
                    href={api.csvExportUrl({ from, to, projectId: projectId || undefined })}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="secondary">
                      <Download className="h-4 w-4" /> CSV
                    </Button>
                  </a>
                  <a
                    href={api.pdfReportUrl({ from, to, projectId: projectId || undefined })}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button variant="secondary">
                      <Download className="h-4 w-4" /> PDF
                    </Button>
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {!hideTabs && (
          <div className="border-b border-border-subtle">
            <nav className="flex flex-wrap gap-1 -mb-px">
              <TabButton
                id="list"
                current={tab}
                icon={List}
                label="Liste"
                embedded={embedded}
                onSelect={lockedTab ? () => {} : setTab}
                disabled={lockedTab && tab !== 'list'}
              />
              <TabButton
                id="range"
                current={tab}
                icon={PieChart}
                label="Zeitraum-Report"
                embedded={embedded}
                onSelect={lockedTab ? () => {} : setTab}
                disabled={lockedTab && tab !== 'range'}
              />
            </nav>
          </div>
        )}

        {showFilters && (
          <div className="space-y-3">
            <div
              className={cn(
                'gap-3',
                embedded
                  ? // flex-wrap with min-width children adapts naturally to the
                    // widget's column width — no breakpoint guessing required.
                    'flex flex-wrap [&>div]:flex-1 [&>div]:min-w-[140px]'
                  : 'grid sm:grid-cols-[auto_auto_1fr_1fr]',
              )}
            >
              <div className="space-y-1">
                <label
                  htmlFor="filter-from"
                  className="text-2xs uppercase tracking-wider text-text-tertiary leading-none"
                >
                  von
                </label>
                <Input
                  id="filter-from"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label
                  htmlFor="filter-to"
                  className="text-2xs uppercase tracking-wider text-text-tertiary leading-none"
                >
                  bis
                </label>
                <Input
                  id="filter-to"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
              <div className={cn('space-y-1', embedded && 'col-span-2')}>
                <label
                  htmlFor="filter-project"
                  className="text-2xs uppercase tracking-wider text-text-tertiary leading-none"
                >
                  Projekt
                </label>
                <Select
                  id="filter-project"
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  <option value="">Alle</option>
                  {projects.map((p) => {
                    const cust = customerMap.get(p.customerId)
                    return (
                      <option key={p.id} value={p.id}>
                        {cust ? `${cust.name} – ` : ''}
                        {p.name}
                      </option>
                    )
                  })}
                </Select>
              </div>
              {showSearchTags ? (
                <div className={cn('space-y-1', embedded && 'col-span-2')}>
                  <label
                    htmlFor="filter-search"
                    className="text-2xs uppercase tracking-wider text-text-tertiary leading-none"
                  >
                    Suche
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary pointer-events-none" />
                    <input
                      id="filter-search"
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Beschreibung oder Notizen"
                      className="flex h-10 w-full rounded-md border border-border-default bg-bg-overlay pl-9 pr-9 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30"
                    />
                    {search && (
                      <button
                        type="button"
                        onClick={() => setSearch('')}
                        aria-label="Suche leeren"
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-tertiary hover:text-text-primary"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ) : embedded ? null : (
                <div />
              )}
            </div>
            {showSearchTags && tags.length > 0 && (
              <TagPicker
                tags={tags}
                selectedIds={tagFilter}
                onToggle={(id) =>
                  setTagFilter((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                  )
                }
              />
            )}
          </div>
        )}

        {tab === 'list' && (
          <>
            {/* Σ Zeitraum kompakt — im embedded mode (CalendarInfoPanel)
                gibt's keinen Header der das schon zeigt, also rendern wir
                eine Mini-Zeile direkt über der Tagesliste. */}
            {embedded && entries.length > 0 && (
              <div className="flex items-baseline justify-between gap-2 px-1">
                <span className="text-2xs uppercase tracking-wider text-text-tertiary">
                  Σ Zeitraum
                </span>
                <span className="font-mono tabular-nums text-sm text-accent">
                  {formatDuration(totalMs)}
                </span>
              </div>
            )}
            {!readOnly && selected.size > 0 && (
              <BulkActions
                selectedIds={Array.from(selected)}
                onCleared={() => setSelected(new Set())}
              />
            )}
            {entriesLoading ? (
              <p className="text-text-secondary">Lade …</p>
            ) : groups.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
                <p className="text-text-secondary">Keine Einträge im gewählten Zeitraum.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {visibleGroups.map(([day, dayEntries]) => {
                  const dayMs = dayEntries.reduce((acc, e) => {
                    const end = e.endTime ? new Date(e.endTime).getTime() : Date.now()
                    return acc + (end - new Date(e.startTime).getTime())
                  }, 0)
                  return (
                    <section key={day}>
                      <div className="mb-2 flex items-baseline justify-between">
                        <h2 className="text-sm font-medium text-text-secondary">
                          {formatEntryDate(day)}
                        </h2>
                        <p className="font-mono text-xs text-text-tertiary tabular-nums">
                          Σ {formatDuration(dayMs)}
                        </p>
                      </div>
                      <ul className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
                        {dayEntries.map((e, i) => {
                          const project = e.projectId ? (projectMap.get(e.projectId) ?? null) : null
                          const customer = e.projectId
                            ? (customerMap.get(projectMap.get(e.projectId)?.customerId ?? '') ??
                              null)
                            : null
                          const isLast = i === dayEntries.length - 1
                          // dayEntries ist desc(startTime): der nächste Eintrag in der
                          // Liste (i+1) ist der zeitlich FRÜHERE. Pause = Start(dieser) −
                          // Ende(früherer). Nur ab MIN_GAP_MINUTES anzeigen.
                          const earlier = isLast ? null : dayEntries[i + 1]
                          const gap = earlier ? gapMinutes(e.startTime, earlier.endTime) : null
                          return (
                            <Fragment key={e.id}>
                              <EntryListItem
                                entry={e}
                                project={project}
                                customer={customer}
                                tagMap={tagMap}
                                isLast={isLast}
                                selected={selected.has(e.id)}
                                onSelectToggle={() =>
                                  setSelected((prev) => {
                                    const next = new Set(prev)
                                    if (next.has(e.id)) next.delete(e.id)
                                    else next.add(e.id)
                                    return next
                                  })
                                }
                                onEdit={() => setDialog({ open: true, entry: e })}
                                compact={embedded}
                                readOnly={readOnly}
                              />
                              {gap !== null && gap >= MIN_GAP_MINUTES && <BreakRow minutes={gap} />}
                            </Fragment>
                          )
                        })}
                      </ul>
                    </section>
                  )
                })}
                {embedded && (
                  <MiniPager
                    page={listPage}
                    totalPages={totalListPages}
                    onPage={setListPage}
                    pageSize={embeddedDaysPerPage}
                    onPageSize={(n) => {
                      setEmbeddedDaysPerPage(n)
                      setListPage(1)
                    }}
                    pageSizeOptions={[3, 5, 7, 14]}
                  />
                )}
              </div>
            )}
          </>
        )}

        {tab === 'range' &&
          (rangeLoading ? (
            <p className="text-text-secondary">Lade …</p>
          ) : rangeData && rangeData.byProject.length > 0 ? (
            <RangeReportView range={rangeData} compact={embedded} />
          ) : (
            <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
              <p className="text-text-secondary">Keine Einträge im gewählten Zeitraum.</p>
            </div>
          ))}
      </div>

      <EntryFormDialog
        open={dialog.open}
        entry={dialog.entry}
        onClose={() => setDialog({ open: false, entry: null })}
        forUserId={userId}
      />
    </>
  )
}

interface TabButtonProps {
  id: TabId
  current: TabId
  icon: typeof List
  label: string
  embedded?: boolean
  onSelect: (id: TabId) => void
  disabled?: boolean
}

function TabButton({
  id,
  current,
  icon: Icon,
  label,
  embedded,
  onSelect,
  disabled,
}: TabButtonProps) {
  const active = id === current
  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect(id)}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-2 border-b-2 transition-colors',
        embedded ? 'px-3 py-2 text-xs' : 'px-4 py-2.5 text-sm',
        active
          ? 'border-accent text-accent font-medium'
          : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-default',
        disabled &&
          'opacity-40 cursor-not-allowed hover:border-transparent hover:text-text-secondary',
      )}
    >
      <Icon className={embedded ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
      {label}
    </button>
  )
}
